import { Glyph, type GlyphName } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { Button, ButtonText } from '@/ui/core/button'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { View } from 'react-native'

/**
 * Whether a member can do anything about it.
 *
 * The distinction is the whole component. `failed` is ours — the network went, the request threw —
 * and pressing the button might fix it. `gone` is nobody's: a form already submitted, a visit that
 * moved, a link that went stale. Offering Retry there sends someone round a loop they cannot win,
 * so the two are not one state with different copy; they are different states, and the type says
 * so rather than trusting each screen to remember.
 */
type Kind = 'failed' | 'gone'

/** What the block is drawn inside: a page it fills, or a sheet it sits in the middle of. */
type Fit = 'page' | 'sheet'

const DEFAULT_LABEL: Record<Kind, string> = { failed: 'Try Again', gone: 'Back' }

/**
 * The mark's disc, which is the only ornament here.
 *
 * `failed` always wears the info glyph, because one failure looks like another. `gone` wears the
 * thing that is missing — a calendar for a visit, a form for a form — so a member reads what has
 * happened before they read the words.
 */
const MARK = 54
const MARK_GLYPH = 26

const Mark = ({ glyph }: { glyph: GlyphName }) => (
	<View
		className="items-center justify-center rounded-full bg-blush"
		style={{ width: MARK, height: MARK }}
	>
		<Glyph name={glyph} size={MARK_GLYPH} color={colors.ink} />
	</View>
)

/**
 * One block for every dead end in the app.
 *
 * It replaced seven hand-written versions that disagreed with each other — three different labels
 * for the same act (`Retry`, `Try Again`, `Back home`), two screens that offered a retry for
 * something that could never succeed, and one that stated the problem and offered nothing at all.
 *
 * Centred, and deliberately: left-aligned type floating in the middle of an otherwise empty page
 * reads as a layout that failed rather than as a thing that was designed, and centred copy is
 * robust to length — which matters when seven screens share one block and nobody re-checks the
 * wrapping on each of them.
 *
 * The title is set in the body face rather than the display one. An error should not be the
 * loudest thing a member has seen all session, and at display size it out-shouts the page it
 * replaced.
 */
export const ErrorState = ({
	kind = 'failed',
	glyph,
	title,
	line,
	onAction,
	actionLabel,
	fit = 'page',
}: {
	kind?: Kind
	/** Required for `gone`, where the mark is the subject. `failed` falls back to the info mark. */
	glyph?: GlyphName
	title: string
	line: string
	onAction: () => void
	/** Overrides the kind's own word, for a way out that is neither a retry nor a step back. */
	actionLabel?: string
	fit?: Fit
}) => {
	const page = fit === 'page'

	return (
		<VStack
			gap={0}
			className={page ? 'flex-1 items-center justify-center px-6' : 'items-center px-4'}
			style={page ? undefined : { paddingTop: 12, paddingBottom: 4 }}
		>
			<Mark glyph={glyph ?? 'info'} />
			<Text
				size={page ? 'body0' : 'body1'}
				weight="bold"
				className="text-center"
				style={{ marginTop: 22 }}
			>
				{title}
			</Text>
			<Text size="body1" tone="soft" className="text-center" style={{ marginTop: 8 }}>
				{line}
			</Text>
			<View style={{ marginTop: 22 }}>
				{/* Outlined, never solid. The app spends its one filled button on the single thing
				    a screen wants you to do, and that is never a failure. */}
				<Button variant="secondary" fullWidth={false} onPress={onAction}>
					<ButtonText>{actionLabel ?? DEFAULT_LABEL[kind]}</ButtonText>
				</Button>
			</View>
		</VStack>
	)
}
